import { and, eq } from "drizzle-orm";

import { createLogger } from "@/logger";

const log = createLogger("call");

import React, { createContext, useCallback, useEffect, useRef } from "react";
import { useBeforeUnload } from "react-router-dom";

import { idx, rowsByIndex, rowsWhere, store } from "@/store/store";
import type { AudioEncoding, Transcription } from "@/store/types";
import { browserIsSafari, type SetStateType } from "@/utils";
import { targetCodecForBrowser } from "@/utils/audio";
import { selectAudio } from "@/utils/contentSelection";
import type { AllPreferredLanguage, PreferredLanguage } from "./languages";

function createSourceElement(filename: string) {
  const contentType = "audio/ogg";
  const src = filename;
  const srcElement = document.createElement("source");
  srcElement.type = contentType;
  srcElement.src = src;
  return srcElement;
}

function createSafariSourceElement(filename: string) {
  const contentType = `audio/x-caf; codecs="opus"`;
  const src = filename;
  const srcElement = document.createElement("source");
  srcElement.type = contentType;
  srcElement.src = src;
  return srcElement;
}

const loadAudioLibrary = async () => {
  if (document !== undefined) {
    try {
      log.info("loading audio library");
      return await import("@storyboard-fm/audio-core-library").then((module) => module);
    } catch (e) {
      log.info(["loadAudioLibrary", e], true);
    }
  } else {
    log.info("document is undefined, cannot load audio library", true);
  }
};

const audioElement = new Audio();

type AudioState = {
  audioCtx?: AudioContext;
  audioElement?: HTMLAudioElement;
  activeItemId?: string;
  setActiveItemId: SetStateType<string | undefined>;
  pauseItem: () => void;
  playItem: () => void;
  playerPlaying: boolean;
  closePlayer: () => void;
};

export const AudioAppContext = createContext<AudioState>({
  audioElement,
  setActiveItemId: () => {},
  activeItemId: null,
  pauseItem: () => {},
  playItem: () => {},
  playerPlaying: false,
  closePlayer: () => {},
});

const mimeTypeOverride = {
  opus: "audio/ogg; codecs='opus'",
  caf: "audio/x-caf; codecs='opus'",
};

function sourcesFromItem(audioSources: AudioEncoding[]) {
  const sourceUrls = [];
  const opus = audioSources.filter((c) => c.codec === "opus")[0];
  const caf = audioSources.filter((c) => c.codec === "caf")[0];
  const mp3 = audioSources.filter((c) => c.codec === "mp3")[0];
  for (const af of [mp3, opus, caf]) {
    if (af) {
      sourceUrls.push({
        src: af.url,
        type: af.codec in mimeTypeOverride ? mimeTypeOverride[af.codec] : af.mimeType,
      });
    }
  }
  const sources = sourceUrls.map((src) => {
    const source = document.createElement("source");
    source.setAttribute("src", src.src);
    if (src.type) {
      source.setAttribute("type", src.type);
    }
    return source;
  });
  return sources;
}

type Props = {
  children: React.ReactNode | React.ReactNode[];
  handsFreeModeFlag: boolean;
};

function clearSources() {
  if (audioElement.hasChildNodes()) {
    const children = audioElement.childNodes;
    for (const child of children) {
      audioElement.removeChild(child);
    }
  }
  if (browserIsSafari(navigator.userAgent)) {
    audioElement.src = "";
  }
}

/** The language the signed-in account listens in, read from the store at call time. */
function currentPreferredLanguage(): PreferredLanguage {
  const myAccount = rowsWhere("account", (row) => row.mine)[0];
  return (myAccount?.preferredLanguage || "none") as PreferredLanguage;
}

/**
 * Loads the audio element with the active item's encoding for the account's language.
 *
 * @param activeItemId - The item whose audio to load.
 * @returns The language the sources were selected for, or null when nothing was loaded.
 */
async function loadAudioSources(activeItemId: string): Promise<PreferredLanguage | null> {
  try {
    if (!activeItemId) {
      log.warn("No active item");
      return null;
    }
    const itemRecord = store.get("item", activeItemId);

    const preferredLanguage = currentPreferredLanguage();

    if (!itemRecord) {
      log.warn("No item");
      return null;
    }

    const targetCodec = targetCodecForBrowser();
    const audioContents = rowsByIndex("audioEncoding", idx.audioEncodingsByContent, itemRecord.contentId).filter(
      (encoding) => encoding.codec === targetCodec,
    );

    // The old drizzle query compared `translatedFrom = NULL`, which never matches in SQL — the
    // original transcription was never found and `inputLanguage` was always undefined. Preserved
    // as-is so audio-language selection behavior doesn't change in this migration.
    const originalTranscription: Transcription | undefined = undefined;

    const inputLanguage = originalTranscription?.language as AllPreferredLanguage;

    log.info({ audioContents });
    const audioContent = selectAudio(preferredLanguage, audioContents, inputLanguage);

    if (audioContent.length === 0) {
      log.warn("No audio sources, skipping item");
      return null;
    }

    let srcElement;
    let srcUrl;

    const sources = sourcesFromItem(audioContent);

    if (browserIsSafari(navigator.userAgent)) {
      // Safari source element
      try {
        srcUrl = sources.filter((cs) => cs.src?.endsWith("caf") === true)?.[0].src;
      } catch (err) {
        srcUrl = sources?.[0].src?.replace(".mp3", ".caf");
      }
      srcElement = createSafariSourceElement(srcUrl);
    } else {
      // Source element for all non-Safari browsers
      srcUrl = sources[0]?.src;
      srcElement = createSourceElement(srcUrl);
    }

    try {
      clearSources();
      // No matter what UA we are we still append the source element
      audioElement.appendChild(srcElement);

      if (browserIsSafari(navigator.userAgent)) {
        audioElement.preload = "none"; // usually Safari ignores what you set here anyway
        // Safari needs this to be set explicitly as well
        audioElement.src = srcUrl;
      } else {
        // here we test only appending the source element for non-Safari
        audioElement.appendChild(srcElement);
      }
      // muted before audio is loaded for safari
      audioElement.muted = true;
      audioElement.load();
      return preferredLanguage;
    } catch (e) {
      log.error("** err thrown during loadAudioSources funcion", e);
      return null;
    }
  } catch (e) {
    log.error("Could not load audio", e);
    return null;
  }
}

const AudioAppContextProvider = ({ children, handsFreeModeFlag }: Props) => {
  const [audioCtx, setAudioCtx] = React.useState<AudioContext>();

  const [playerPlaying, setPlayerPlaying] = React.useState<boolean>(false);
  const [activeItemId, setActiveItemId] = React.useState<string>();

  // Tracks the most recent audioElement.play() promise (with .catch attached) so that
  // pause()/load() callers can await it and avoid AbortError: "The play() request was
  // interrupted by a call to pause()" surfacing as an unhandled rejection.
  const playPromiseRef = useRef<Promise<unknown> | null>(null);

  // The item and language the audio element's sources were last loaded for, so replaying the active item after a
  // language change reloads it in the new language.
  const loadedLanguageRef = useRef<{ itemId: string; language: PreferredLanguage | null } | null>(null);

  // The active item as of the latest render, so a reload that awaited a pending play can tell the user moved on.
  const activeItemIdRef = useRef(activeItemId);
  activeItemIdRef.current = activeItemId;

  const awaitPendingPlay = useCallback((cb: () => void) => {
    const pending = playPromiseRef.current;
    if (pending) {
      pending.then(cb);
    } else {
      cb();
    }
  }, []);

  const resetPlayer = useCallback(() => {
    setActiveItemId(null);
    setPlayerPlaying(false);
    if (!audioElement) return;
    awaitPendingPlay(() => {
      audioElement.pause();
      audioElement.currentTime = 0;
      clearSources();
      audioElement.load();
    });
  }, [awaitPendingPlay]);

  useEffect(() => {
    loadAudioLibrary()
      ?.then(() => {
        const audioCtx = new AudioContext();
        log.info(["audioCtx", audioCtx]);
        audioCtx.toggle();
        setAudioCtx(audioCtx);
      })
      .catch((e) => log.error(["Error loading audio library", e], true));
  }, []);

  const startPlayback = useCallback(() => {
    if (!audioElement) return;
    audioElement.muted = false;
    const play = audioElement.play();
    if (play === undefined) return;
    const tracked = play
      .then(() => {
        setPlayerPlaying(true);
      })
      .catch(() => {
        // Either autoplay was prevented or pause()/load() interrupted the play
        // request (AbortError). Either way, we just reflect that we're not playing.
        setPlayerPlaying(false);
      })
      .finally(() => {
        if (playPromiseRef.current === tracked) {
          playPromiseRef.current = null;
        }
      });
    playPromiseRef.current = tracked;
  }, []);

  // Plays the active item, first reloading its sources when the language changed since they were loaded. Playback
  // already under way finishes in the language it started in; a paused item restarts from the top in the new one.
  const playItem = useCallback(() => {
    if (!audioElement) return;
    const loaded = loadedLanguageRef.current;
    const stale =
      activeItemId != null &&
      loaded?.itemId === activeItemId &&
      audioElement.paused &&
      loaded.language !== currentPreferredLanguage();
    if (!stale) {
      startPlayback();
      return;
    }
    (async () => {
      const pendingPlay = playPromiseRef.current;
      if (pendingPlay) await pendingPlay;
      // Another item became active meanwhile; its own effect loads and plays it.
      if (activeItemIdRef.current !== activeItemId) return;
      const language = await loadAudioSources(activeItemId);
      loadedLanguageRef.current = { itemId: activeItemId, language };
      startPlayback();
    })();
  }, [activeItemId, startPlayback]);

  const pauseItem = useCallback(() => {
    if (!audioElement) return;
    awaitPendingPlay(() => {
      audioElement.pause();
      setPlayerPlaying(false);
    });
  }, [awaitPendingPlay]);

  useEffect(() => {
    if (audioElement) {
      audioElement.onended = () => {
        setPlayerPlaying(false);
        audioElement.currentTime = 0;
      };
    }
  }, []);

  const prevActiveItemId = useRef<string | undefined>(undefined);
  useEffect(() => {
    const f = async () => {
      // Wait for any in-flight play() to settle before clearing/reloading sources;
      // otherwise audioElement.load() inside loadAudioSources can reject the previous
      // play promise with AbortError mid-flight.
      const pendingPlay = playPromiseRef.current;
      if (pendingPlay) await pendingPlay;
      if (activeItemId) {
        prevActiveItemId.current = activeItemId;
        const language = await loadAudioSources(activeItemId);
        loadedLanguageRef.current = { itemId: activeItemId, language };
        startPlayback();
      } else if (prevActiveItemId.current) {
        prevActiveItemId.current = undefined;
        loadedLanguageRef.current = null;
        resetPlayer();
      }
    };
    f();
  }, [activeItemId]);

  useBeforeUnload(() => {
    resetPlayer();
  });

  const audioState: AudioState = {
    audioElement,
    setActiveItemId,
    activeItemId,
    pauseItem,
    playItem,
    playerPlaying,
    closePlayer: resetPlayer,
  };

  return <AudioAppContext.Provider value={audioState}>{children}</AudioAppContext.Provider>;
};

export default AudioAppContextProvider;
